import React from 'react'
import { Box, Typography, LinearProgress, Tooltip } from '@mui/material'
import { GridRenderCellParams, GridColDef } from '@mui/x-data-grid'

const getMilesByStateTableColumns = (): GridColDef[] => {
  return [
    {
      field: 'country',
      headerName: 'Country',
      width: 65,
      minWidth: 65,
      flex: 0.4,
      sortable: true,
      align: 'left',
      headerAlign: 'left',
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
            width: '100%',
            py: 0.5
          }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {params.row.state?.country || '-'}
            </Typography>
          </Box>
        )
      },
      valueGetter: (value: any, row: any) => {
        return row.state?.country || '-'
      },
    },
    {
      field: 'state',
      headerName: 'State',
      width: 180,
      minWidth: 180,
      flex: 1.5,
      sortable: true,
      renderCell: function (params: GridRenderCellParams) {
        const fullText = `${params.row.state.code} ${params.row.state.name}`
        return (
          <Tooltip title={fullText} placement="top">
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              py: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: '24px' }}>
                {params.row.state.code}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {params.row.state.name}
              </Typography>
            </Box>
          </Tooltip>
        )
      },
      valueGetter: (value: any, row: any) => {
        return `${row.state?.code} ${row.state?.name}`
      },
    },
    {
      field: 'miles',
      headerName: 'Miles',
      type: 'number',
      width: 100,
      minWidth: 100,
      flex: 0.7,
      sortable: true,
      headerClassName: 'columnGroupTitle centered',
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Box
            sx={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              height: '100%',
              py: 0.5,
            }}
          >
            <Typography variant="body2">
              {params.row.metrics?.distanceMiles?.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              }) || 0}
            </Typography>
          </Box>
        )
      },
      valueGetter: (value: any, row: any) => {
        return row.metrics?.distanceMiles || 0
      },
    },
    {
      field: 'percentage',
      headerName: '%',
      type: 'number',
      width: 150,
      minWidth: 150,
      headerClassName: 'columnGroupTitle centered',
      flex: 0.9,
      sortable: true,
      renderCell: function (params: GridRenderCellParams) {
        const minBarHeight = 10;
        const maxBarHeight = 100;
        const percentage = Math.min(100, Math.max(0, params.row.metrics.percentageOfTotal));
        const scaledPercentage = minBarHeight + (percentage / 100) * (maxBarHeight - minBarHeight);

        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
            height: '100%',
            py: 0.5
          }}>
            <LinearProgress
              variant="determinate"
              value={scaledPercentage}
              sx={{
                flexGrow: 1,
                height: 4,
                borderRadius: 3,
                backgroundColor: '#f3f4f6',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'primary',
                  borderRadius: 3,
                },
              }}
            />
            <Typography variant="body2" sx={{ minWidth: '40px', fontSize: '0.875rem' }}>
              {params.row.metrics?.percentageOfTotal?.toFixed(2) || 0}%
            </Typography>
          </Box>
        )
      },
      valueGetter: (value: any, row: any) => {
        return row.metrics?.percentageOfTotal || 0
      },
    },
  ]
}

export { getMilesByStateTableColumns }
